<template>
  <div ref="novuInbox"></div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { NovuUI } from '@novu/js/ui';
import { useNovuCredentials } from '@/queries/use-novu-credentials';
import { tailwindTheme as theme } from '@carvia/theme/lib/tailwind-theme.ts';
import { button } from '@carvia/theme/lib/cva/button';
import { useRouter } from 'vue-router';

// Create a reactive reference to hold the DOM element
const novuInbox = ref<HTMLElement | null>(null);
// Store the Novu instance for cleanup during unmount
let novuInstance: NovuUI | null = null;

// Use the Novu credentials hook
const { data: credentials, isLoading } = useNovuCredentials();

const router = useRouter();

// Don't subscribe when we have test/E2E credentials (backend returns this in test mode)
const isTestCredentials = (cred: typeof credentials.value) => cred?.hmacHash === 'e2e-test-hash';

// Function to mount Novu instance
const mountNovu = () => {
  if (!novuInbox.value || !credentials.value || isTestCredentials(credentials.value)) {
    return;
  }

  try {
    // Initialize the Novu UI instance with credentials from the hook
    const novu = new NovuUI({
      options: {
        applicationIdentifier: import.meta.env.VITE_NOVU_APP_IDENTIFIER,
        subscriberId: credentials.value?.subscriberId,
        subscriberHash: credentials.value?.hmacHash,
        apiUrl: import.meta.env.VITE_NOVU_BASE_URL,
        socketUrl: import.meta.env.VITE_NOVU_SOCKET_BASE_URL,
      },
      appearance: {
        variables: {
          colorPrimary: (theme as any)?.colors?.hazel[500],
          colorPrimaryForeground: (theme as any)?.colors?.obsidian[800],
          colorSecondaryForeground: (theme as any)?.colors?.obsidian[800],
          fontSize: '1rem',
        },
        elements: {
          notificationPrimaryAction__button:
            button({ size: 'sm', variant: 'secondary' }) +
            ' !border-none !shadow-none !bg-none before:hidden after:hidden',
          inboxHeader: 'bg-white !border-none',
          notificationList: '!border-top-none',
        },
      },
    });

    // Mount the Inbox component to our div reference
    novu.mountComponent({
      name: 'Inbox',
      props: {
        onNotificationClick: (notification) => {
          if (notification.redirect?.url) {
            router.push({ path: notification.redirect.url });
          }
        },
        onPrimaryActionClick: (notification) => {
          if (notification.primaryAction?.redirect?.url) {
            router.push({ path: notification.primaryAction?.redirect.url });
          }
        },
        onSecondaryActionClick: (notification) => {
          if (notification.secondaryAction?.redirect?.url) {
            router.push({ path: notification.secondaryAction?.redirect.url });
          }
        },
      },
      element: novuInbox.value,
    });

    // Store the instance for cleanup
    novuInstance = novu;
  } catch (error) {
    console.error('Failed to initialize Novu inbox:', error);
  }
};

// Function to unmount Novu instance
const unmountNovu = () => {
  if (novuInstance && novuInbox.value) {
    try {
      novuInstance.unmountComponent(novuInbox.value);
      novuInstance = null;
    } catch (error) {
      console.error('Failed to unmount Novu inbox:', error);
    }
  }
};

// Watch for changes in credentials and mount/unmount accordingly
watch(
  credentials,
  (newCredentials) => {
    if (newCredentials && !isTestCredentials(newCredentials)) {
      mountNovu();
    } else {
      unmountNovu();
    }
  },
  { immediate: true },
);

onMounted(() => {
  if (credentials.value) {
    mountNovu();
  }
});

// Clean up when the component is destroyed
onUnmounted(() => {
  unmountNovu();
});
</script>
